import React, { useContext, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import combos from '../data/combos';
import BoxContext from '../context/BoxContext';
import BoxAnimation from '../components/BoxAnimation';

/**
 * Página de detalle de combo con animaciones mejoradas.
 * Busca un combo según el parámetro de la URL y muestra toda su información.
 */
export default function ComboDetails() {
  const { id } = useParams();
  const { addToBox } = useContext(BoxContext);

  // Estado para animar el mensaje de producto añadido
  const [showAddedMessage, setShowAddedMessage] = useState(false);
  // Buscar el combo correspondiente por slug
  const combo = combos.find((c) => c.slug === id);

  // Función para formatear precio en pesos chilenos
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Si no se encuentra, mostrar mensaje de error
  if (!combo) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="container mx-auto px-4 py-12"
      >
        <h1 className="text-3xl font-bold mb-4 text-primary">Combo no encontrado</h1>
        <p className="mb-6">Lo sentimos, el combo solicitado no existe.</p>
        <Link to="/" className="text-primary underline">Volver al inicio</Link>
      </motion.div>
    );
  }

  const handleAdd = () => {
    // Agregar el combo completo a la caja
    addToBox(combo);
    // Mostrar mensaje con animación
    setShowAddedMessage(true);
    setTimeout(() => {
      setShowAddedMessage(false);
    }, 1500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-12 relative"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl md:text-4xl font-bold text-primary"
        >
          {combo.name}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-primary text-white px-6 py-3 rounded-lg text-2xl font-bold shadow-lg"
        >
          {formatPrice(combo.price)}
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-gray-700 mb-2 font-medium text-lg"
      >
        {combo.persons}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-gray-600 mb-6 max-w-prose text-lg"
      >
        {combo.description}
      </motion.p>

      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-2xl font-semibold mb-3"
      >
        Incluye:
      </motion.h2>

      <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8">
        {combo.items.map((item, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 + index * 0.05 }}
            className="text-base"
          >
            {item}
          </motion.li>
        ))}
      </ul>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex flex-wrap gap-4"
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAdd}
          className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors focus:outline-none shadow-lg"
        >
          🛒 Agregar al Carrito
        </motion.button>

        <Link to="/">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-secondary text-primary px-6 py-3 rounded-lg font-semibold hover:bg-secondary-dark transition-colors shadow-lg"
          >
            ← Volver
          </motion.button>
        </Link>
      </motion.div>

      {/* Animación de producto agregado */}
      <BoxAnimation show={showAddedMessage} />
    </motion.div>
  );
}